/**
 * Financial Astrology — timing calendar for market windows.
 * Client-side: lunar phases are computed; planetary windows are curated from
 * published ephemerides (Swiss Ephemeris–class dates). Educational overlay only.
 * S&P overlay uses SPY daily closes from the proxy (same cache as Analytics).
 */
import "./financialAstrology.css";
import { getApiConfig } from "../config";
import { proxyFetch } from "../api/proxyFetch";

export type AstrologyPosture = "calm" | "watch" | "caution" | "alert";

export type AstrologyTag =
  | "volatility"
  | "reversal"
  | "comms"
  | "valuation"
  | "trend"
  | "liquidity"
  | "sentiment";

export interface AstrologyWindow {
  id: string;
  title: string;
  kind: "lunar" | "mercury" | "venus" | "eclipse" | "ingress" | "aspect" | "station";
  start: string; // YYYY-MM-DD (UTC calendar day)
  end: string;
  intensity: 1 | 2 | 3;
  tags: AstrologyTag[];
  summary: string;
  traderNote: string;
}

export interface AstrologyPosturePoint {
  date: string;
  score: number;
  posture: AstrologyPosture;
}

export interface AstrologySpxPoint {
  date: string;
  close: number;
}

export interface AstrologySnapshot {
  ok: true;
  updatedAt: string;
  asOf: string;
  year: number;
  posture: AstrologyPosture;
  score: number;
  sentence: string;
  lunar: {
    phaseName: string;
    illuminationPct: number;
    nextNewMoon: string | null;
    nextFullMoon: string | null;
    daysToNew: number | null;
    daysToFull: number | null;
  };
  active: AstrologyWindow[];
  upcoming: AstrologyWindow[];
  playbook: Array<{ title: string; body: string }>;
  footnotes: string[];
}

const TAG_LABEL: Record<AstrologyTag, string> = {
  volatility: "Volatility",
  reversal: "Reversal",
  comms: "Comms / ops",
  valuation: "Valuation",
  trend: "Trend turn",
  liquidity: "Liquidity",
  sentiment: "Sentiment",
};

const POSTURE_LABEL: Record<AstrologyPosture, string> = {
  calm: "Calm",
  watch: "Watch",
  caution: "Caution",
  alert: "Alert",
};

function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** UTC calendar YYYY-MM-DD */
function ymdUTC(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function addDaysYmd(ymd: string, days: number): string {
  const d = parseYmd(ymd);
  d.setUTCDate(d.getUTCDate() + days);
  return ymdUTC(d);
}

function daysBetween(a: string, b: string): number {
  return Math.round((parseYmd(b).getTime() - parseYmd(a).getTime()) / 86_400_000);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Julian Day Number (UTC noon convention via fractional day). */
function julianDay(date: Date): number {
  const y = date.getUTCFullYear();
  let m = date.getUTCMonth() + 1;
  const d =
    date.getUTCDate() +
    (date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600) / 24;
  let yy = y;
  if (m <= 2) {
    yy -= 1;
    m += 12;
  }
  const A = Math.floor(yy / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (yy + 4716)) + Math.floor(30.6001 * (m + 1)) + d + B - 1524.5;
}

function illuminationAt(date: Date): number {
  const jd = julianDay(date);
  const k = (jd - 2451550.09765) / 29.530588853;
  const phase = ((k % 1) + 1) % 1; // 0 = new, 0.5 = full
  return clamp((1 - Math.cos(2 * Math.PI * phase)) / 2, 0, 1);
}

function phaseName(illum: number, waxing: boolean): string {
  if (illum < 0.03) return "New Moon";
  if (illum > 0.97) return "Full Moon";
  if (illum < 0.22) return waxing ? "Waxing crescent" : "Waning crescent";
  if (illum < 0.28) return waxing ? "First quarter" : "Last quarter";
  if (illum < 0.72) return waxing ? "Waxing gibbous" : "Waning gibbous";
  return waxing ? "Waxing gibbous" : "Waning gibbous";
}

function isWaxing(date: Date): boolean {
  const jd = julianDay(date);
  const k = (jd - 2451550.09765) / 29.530588853;
  const phase = ((k % 1) + 1) % 1;
  return phase < 0.5;
}

function nextLunarExtremes(from: Date): { nextNew: Date; nextFull: Date } {
  const jd = julianDay(from);
  let kn = Math.ceil((jd - 2451550.09765) / 29.530588853);
  let kf = Math.ceil((jd - 2451550.09765) / 29.530588853 - 0.5) + 0.5;
  let newJd = 2451550.09765 + 29.530588853 * kn;
  let fullJd = 2451550.09765 + 29.530588853 * kf;
  // Ensure strictly in the future (or today)
  while (newJd < jd - 0.05) {
    kn += 1;
    newJd = 2451550.09765 + 29.530588853 * kn;
  }
  while (fullJd < jd - 0.05) {
    kf += 1;
    fullJd = 2451550.09765 + 29.530588853 * kf;
  }
  const toDate = (j: number) => new Date((j - 2440587.5) * 86_400_000);
  return { nextNew: toDate(newJd), nextFull: toDate(fullJd) };
}

function lunarWindows(fromYmd: string, horizonDays: number): AstrologyWindow[] {
  const out: AstrologyWindow[] = [];
  const start = parseYmd(fromYmd);
  const end = parseYmd(addDaysYmd(fromYmd, horizonDays));
  let cursor = new Date(start.getTime() - 40 * 86_400_000);
  let guard = 0;
  while (guard++ < 40) {
    const { nextNew, nextFull } = nextLunarExtremes(cursor);
    for (const [kind, when, title, summary, note] of [
      [
        "new",
        nextNew,
        "New Moon",
        "Fresh lunar cycle — historically tied to quieter opens and idea-generation, not a guaranteed dip.",
        "Favor planning new put candidates; avoid forcing size on the exact day if liquidity is thin.",
      ],
      [
        "full",
        nextFull,
        "Full Moon",
        "Peak lunar illumination — often framed as elevated emotion / follow-through risk around risk assets.",
        "Tighten management on short premium; watch for exaggerated reactions into Friday/Monday overlaps.",
      ],
    ] as const) {
      const day = ymdUTC(when);
      if (day < fromYmd || when > end) continue;
      if (out.some((w) => w.id === `lunar-${kind}-${day}`)) continue;
      out.push({
        id: `lunar-${kind}-${day}`,
        title,
        kind: "lunar",
        start: day,
        end: day,
        intensity: 1,
        tags: kind === "full" ? ["sentiment", "volatility"] : ["sentiment"],
        summary,
        traderNote: note,
      });
    }
    cursor = new Date(Math.min(nextNew.getTime(), nextFull.getTime()) + 2 * 86_400_000);
    if (cursor > end) break;
  }
  return out;
}

/**
 * Curated planetary / eclipse windows (UTC dates from published 2025–2027 ephemerides).
 * Kept as data so the UI stays useful without a live ephemeris service.
 */
const CURATED_WINDOWS: AstrologyWindow[] = [
  // Late 2025 carry
  {
    id: "merc-rx-2025-11",
    title: "Mercury retrograde (Sagittarius)",
    kind: "mercury",
    start: "2025-11-09",
    end: "2025-11-29",
    intensity: 2,
    tags: ["comms", "reversal", "volatility"],
    summary: "Classic reprice / miscommunication window — headlines and order flow can whipsaw.",
    traderNote: "Double-check expirations, fills, and assignment notices. Prefer defined-risk adjusts over heroic rolls.",
  },
  // 2026 ingresses & eclipses
  {
    id: "neptune-aries-2026",
    title: "Neptune enters Aries",
    kind: "ingress",
    start: "2026-01-26",
    end: "2026-01-28",
    intensity: 2,
    tags: ["trend", "sentiment"],
    summary: "Rare slow-planet sign change — long-horizon narrative shift (risk appetite / ideology themes).",
    traderNote: "Treat as backdrop, not a day-trade trigger. Useful for quarterly thesis reviews.",
  },
  {
    id: "saturn-aries-2026",
    title: "Saturn enters Aries",
    kind: "ingress",
    start: "2026-02-13",
    end: "2026-02-15",
    intensity: 2,
    tags: ["trend", "liquidity"],
    summary: "Saturn resumes Aries — accountability / constraint themes in growth and leverage narratives.",
    traderNote: "Revisit margin and concentration. Prefer underlyings you can hold through a slower tape.",
  },
  {
    id: "solar-ecl-2026-02",
    title: "Annular solar eclipse (Aquarius)",
    kind: "eclipse",
    start: "2026-02-16",
    end: "2026-02-18",
    intensity: 3,
    tags: ["trend", "volatility", "reversal"],
    summary: "First eclipse of the year — financial astrology often marks these as trend-seed windows.",
    traderNote: "Avoid max size into the exact day. Map puts/calls you would still want if the tape gaps.",
  },
  {
    id: "saturn-neptune-2026",
    title: "Saturn conjunct Neptune (Aries)",
    kind: "aspect",
    start: "2026-02-19",
    end: "2026-02-21",
    intensity: 3,
    tags: ["trend", "sentiment", "valuation"],
    summary: "Slow-planet conjunction — blur between vision and hard limits; narrative resets in risk assets.",
    traderNote: "Watch crowded themes. Scale premium sales; keep dry powder for dislocation.",
  },
  {
    id: "merc-rx-2026-02",
    title: "Mercury retrograde (Pisces)",
    kind: "mercury",
    start: "2026-02-26",
    end: "2026-03-20",
    intensity: 2,
    tags: ["comms", "reversal", "volatility"],
    summary: "Overlaps the Feb–Mar eclipse season — elevated odds of false breaks and ops friction.",
    traderNote: "Confirm corporate actions and broker notices. Favor liquid underlyings for rolls.",
  },
  {
    id: "lunar-ecl-2026-03",
    title: "Total lunar eclipse (Virgo)",
    kind: "eclipse",
    start: "2026-03-02",
    end: "2026-03-04",
    intensity: 3,
    tags: ["volatility", "reversal", "sentiment"],
    summary: "Closes the early eclipse pair — follow-through or fade of the Feb solar seed.",
    traderNote: "Review open short premium into the window; have BTC levels before the session.",
  },
  {
    id: "uranus-gemini-2026",
    title: "Uranus enters Gemini",
    kind: "ingress",
    start: "2026-04-25",
    end: "2026-04-27",
    intensity: 2,
    tags: ["trend", "comms"],
    summary: "Multi-year tech / information / transport theme shift (Uranus in Gemini into the early 2030s).",
    traderNote: "Useful for sector rotation notes — not a one-day signal.",
  },
  {
    id: "merc-rx-2026-06",
    title: "Mercury retrograde (Cancer)",
    kind: "mercury",
    start: "2026-06-29",
    end: "2026-07-23",
    intensity: 2,
    tags: ["comms", "reversal", "sentiment"],
    summary: "Mid-year reassessment loop — home/cash-flow narratives can dominate headlines.",
    traderNote: "Expect chop around macro prints. Keep DTE buffers on short puts.",
  },
  {
    id: "jupiter-leo-2026",
    title: "Jupiter enters Leo",
    kind: "ingress",
    start: "2026-06-30",
    end: "2026-07-02",
    intensity: 2,
    tags: ["sentiment", "valuation"],
    summary: "Expansion into Leo — appetite for leaders / narrative stocks often gets a longer leash.",
    traderNote: "Watch for overconfidence in crowded longs; still size shorts to assignment risk.",
  },
  {
    id: "saturn-rx-2026",
    title: "Saturn stations retrograde (Aries)",
    kind: "station",
    start: "2026-07-25",
    end: "2026-07-27",
    intensity: 2,
    tags: ["liquidity", "trend"],
    summary: "Saturn station — slowdowns in “prove it” growth stories and leverage narratives.",
    traderNote: "Stress-test margin and roll plans; prefer quality collateral underlyings.",
  },
  {
    id: "solar-ecl-2026-08",
    title: "Total solar eclipse (Leo)",
    kind: "eclipse",
    start: "2026-08-11",
    end: "2026-08-13",
    intensity: 3,
    tags: ["trend", "volatility", "sentiment"],
    summary: "Major second-half inflection window in financial astrology calendars.",
    traderNote: "Reduce lottery-ticket risk into the day; schedule portfolio review after the print week.",
  },
  {
    id: "lunar-ecl-2026-08",
    title: "Partial lunar eclipse (Pisces)",
    kind: "eclipse",
    start: "2026-08-27",
    end: "2026-08-29",
    intensity: 3,
    tags: ["volatility", "reversal"],
    summary: "Follow-through eclipse — confirmation or rejection of the mid-August seed.",
    traderNote: "Manage winners; avoid adding naked short gamma into the exact window.",
  },
  {
    id: "jup-sat-trine-2026",
    title: "Jupiter trine Saturn",
    kind: "aspect",
    start: "2026-08-30",
    end: "2026-09-01",
    intensity: 2,
    tags: ["liquidity", "trend"],
    summary: "Supportive growth-vs-constraint aspect — often framed as steadier risk appetite.",
    traderNote: "Good window to rebuild structured wheel inventory if vol is mid.",
  },
  {
    id: "venus-rx-2026",
    title: "Venus retrograde (Scorpio→Libra)",
    kind: "venus",
    start: "2026-10-03",
    end: "2026-11-13",
    intensity: 3,
    tags: ["valuation", "reversal", "sentiment"],
    summary: "Rare valuation / appetite reset (~18 months). Overlaps late Mercury Rx.",
    traderNote: "Re-rate targets and premium assumptions. Watch luxury, fintech, and crowded multiples.",
  },
  {
    id: "merc-rx-2026-10",
    title: "Mercury retrograde (Scorpio)",
    kind: "mercury",
    start: "2026-10-24",
    end: "2026-11-13",
    intensity: 2,
    tags: ["comms", "reversal", "volatility"],
    summary: "Stacked with Venus Rx — noise, revisions, and narrative flips more likely.",
    traderNote: "Document trade thesis before entering; avoid complex multi-leg repairs on shaky fills.",
  },
  {
    id: "saturn-direct-2026",
    title: "Saturn stations direct (Aries)",
    kind: "station",
    start: "2026-12-09",
    end: "2026-12-11",
    intensity: 2,
    tags: ["trend", "liquidity"],
    summary: "Saturn turns direct — constraints start moving forward again in real economy narratives.",
    traderNote: "Year-end positioning: prefer clean books into the turn.",
  },
];

function windowActiveOn(w: AstrologyWindow, day: string): boolean {
  return w.start <= day && day <= w.end;
}

function windowStartsWithin(w: AstrologyWindow, day: string, horizon: number): boolean {
  const delta = daysBetween(day, w.start);
  return delta >= 0 && delta <= horizon;
}

function postureScoreValue(active: AstrologyWindow[], lunarIllum: number): number {
  let score = 0;
  for (const w of active) {
    score += w.intensity;
    if (w.kind === "eclipse") score += 1;
  }
  if (lunarIllum > 0.92 || lunarIllum < 0.08) score += 0.5;
  return score;
}

function postureFromScore(score: number): AstrologyPosture {
  if (score >= 5) return "alert";
  if (score >= 3) return "caution";
  if (score >= 1.5) return "watch";
  return "calm";
}

/** Windows used for daily posture (curated + lunar turns overlapping the span). */
function windowsForSpan(startYmd: string, endYmd: string): AstrologyWindow[] {
  const horizon = Math.max(0, daysBetween(startYmd, endYmd)) + 2;
  const lunar = lunarWindows(startYmd, horizon).filter((w) => w.start <= endYmd);
  const curated = CURATED_WINDOWS.filter((w) => w.end >= startYmd && w.start <= endYmd);
  return [...curated, ...lunar];
}

function scoreForDay(day: string, pool: readonly AstrologyWindow[]): { score: number; posture: AstrologyPosture } {
  const active = pool.filter((w) => windowActiveOn(w, day));
  const illum = illuminationAt(parseYmd(day));
  const score = postureScoreValue(active, illum);
  return { score, posture: postureFromScore(score) };
}

/** Daily posture score for a calendar year (UTC). Full year including future days. */
export function buildPostureYearHistory(year: number): AstrologyPosturePoint[] {
  const start = `${year}-01-01`;
  const end = `${year}-12-31`;
  const pool = windowsForSpan(start, end);
  const out: AstrologyPosturePoint[] = [];
  let day = start;
  while (day <= end) {
    const { score, posture } = scoreForDay(day, pool);
    out.push({ date: day, score, posture });
    day = addDaysYmd(day, 1);
  }
  return out;
}

function buildSentence(
  posture: AstrologyPosture,
  active: AstrologyWindow[],
  lunarName: string,
): string {
  if (!active.length) {
    return `${POSTURE_LABEL[posture]} tape timing — ${lunarName.toLowerCase()}, no major planetary windows active. Ordinary management rules apply.`;
  }
  const titles = active
    .slice()
    .sort((a, b) => b.intensity - a.intensity)
    .slice(0, 2)
    .map((w) => w.title);
  const join = titles.length === 1 ? titles[0]! : `${titles[0]} + ${titles[1]}`;
  if (posture === "alert" || posture === "caution") {
    return `${POSTURE_LABEL[posture]} window — ${join}. Favor liquidity, defined risk, and pre-planned adjusts.`;
  }
  return `${POSTURE_LABEL[posture]} — ${join} in play under a ${lunarName.toLowerCase()}. Stay mechanical on rolls.`;
}

export function buildFinancialAstrologySnapshot(now = new Date()): AstrologySnapshot {
  const asOf = ymdUTC(now);
  const illum = illuminationAt(now);
  const waxing = isWaxing(now);
  const lunarName = phaseName(illum, waxing);
  const { nextNew, nextFull } = nextLunarExtremes(now);
  const nextNewYmd = ymdUTC(nextNew);
  const nextFullYmd = ymdUTC(nextFull);

  const lunar = lunarWindows(asOf, 90);
  const curated = CURATED_WINDOWS.filter((w) => w.end >= addDaysYmd(asOf, -7));
  const all = [...curated, ...lunar].sort((a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title));

  const active = all.filter((w) => windowActiveOn(w, asOf));
  const upcoming = all
    .filter((w) => !windowActiveOn(w, asOf) && windowStartsWithin(w, asOf, 90))
    .slice(0, 24);

  const score = postureScoreValue(active, illum);
  const posture = postureFromScore(score);

  return {
    ok: true,
    updatedAt: now.toISOString(),
    asOf,
    year: now.getUTCFullYear(),
    posture,
    score,
    sentence: buildSentence(posture, active, lunarName),
    lunar: {
      phaseName: lunarName,
      illuminationPct: Math.round(illum * 100),
      nextNewMoon: nextNewYmd,
      nextFullMoon: nextFullYmd,
      daysToNew: daysBetween(asOf, nextNewYmd),
      daysToFull: daysBetween(asOf, nextFullYmd),
    },
    active,
    upcoming,
    playbook: [
      {
        title: "Eclipse weeks",
        body: "Treat ±1 session as higher gap risk. Prefer liquid underlyings, defined-risk structures, and written adjust plans before the open.",
      },
      {
        title: "Mercury retrograde",
        body: "Ops friction rises (fills, notices, fat-finger). Not a short signal by itself — raise the bar for complex multi-leg repairs.",
      },
      {
        title: "Venus retrograde",
        body: "Valuation and appetite resets (~18 months). Revisit premium targets and crowded multiples rather than chasing mean reversion.",
      },
      {
        title: "Lunar turns",
        body: "New/full moons are mild sentiment markers. Use them as calendar bookmarks for reviews, not as standalone entries.",
      },
      {
        title: "With the wheel",
        body: "Pair this tab with Market gauges and your own-ledger History. Astrology times windows; price and credit decide trades.",
      },
    ],
    footnotes: [
      "Educational timing overlay — not investment advice and not a prediction engine.",
      "Lunar phases are computed locally; planetary windows use published UTC ephemeris dates (may differ by timezone by one session).",
      "Posture scores overlapping windows; they do not forecast direction or magnitude.",
    ],
  };
}

function fmtDay(ymd: string): string {
  const d = parseYmd(ymd);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function fmtRange(start: string, end: string): string {
  if (start === end) return fmtDay(start);
  return `${fmtDay(start)} – ${fmtDay(end)}`;
}

function tagPills(tags: AstrologyTag[]): string {
  return tags.map((t) => `<span class="sfe-astro-tag">${esc(TAG_LABEL[t])}</span>`).join("");
}

function intensityDots(n: number): string {
  return `<span class="sfe-astro-intensity" aria-label="Intensity ${n} of 3">${"●".repeat(n)}${"○".repeat(3 - n)}</span>`;
}

function renderWindowCard(w: AstrologyWindow, mode: "active" | "upcoming"): string {
  const when =
    mode === "active" && w.start !== w.end
      ? `Active through ${fmtDay(w.end)}`
      : fmtRange(w.start, w.end);
  return `<article class="sfe-astro-card sfe-astro-card-${esc(w.kind)} sfe-astro-card-${mode}">
    <div class="sfe-astro-card-top">
      <strong>${esc(w.title)}</strong>
      ${intensityDots(w.intensity)}
    </div>
    <p class="sfe-astro-when">${esc(when)}</p>
    <div class="sfe-astro-tags">${tagPills(w.tags)}</div>
    <p class="sfe-astro-summary">${esc(w.summary)}</p>
    <p class="sfe-astro-note"><span>Trader note</span> ${esc(w.traderNote)}</p>
  </article>`;
}

function monthTicks(year: number): Array<{ date: string; label: string }> {
  const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return labels.map((label, i) => ({
    date: `${year}-${pad2(i + 1)}-01`,
    label,
  }));
}

function stepPathForRange(
  series: readonly AstrologyPosturePoint[],
  fromIdx: number,
  toIdx: number,
  xAt: (i: number) => number,
  yAt: (score: number) => number,
): string {
  if (fromIdx > toIdx || fromIdx < 0 || toIdx >= series.length) return "";
  let d = "";
  for (let i = fromIdx; i <= toIdx; i++) {
    const x = xAt(i);
    const y = yAt(series[i]!.score);
    if (i === fromIdx) d += `M ${x.toFixed(1)} ${y.toFixed(1)}`;
    else {
      const prevY = yAt(series[i - 1]!.score);
      d += ` L ${x.toFixed(1)} ${prevY.toFixed(1)} L ${x.toFixed(1)} ${y.toFixed(1)}`;
    }
  }
  return d;
}

/** Contiguous forecast runs at Caution/Alert — used as prediction callouts. */
function predictedPeakRuns(
  series: readonly AstrologyPosturePoint[],
  asOf: string,
): Array<{ start: string; end: string; peak: AstrologyPosturePoint }> {
  const runs: Array<{ start: string; end: string; peak: AstrologyPosturePoint }> = [];
  let cur: { start: string; end: string; peak: AstrologyPosturePoint } | null = null;
  for (const p of series) {
    if (p.date <= asOf) continue;
    const hot = p.posture === "caution" || p.posture === "alert";
    if (!hot) {
      if (cur) runs.push(cur);
      cur = null;
      continue;
    }
    if (!cur) {
      cur = { start: p.date, end: p.date, peak: p };
    } else {
      cur.end = p.date;
      if (p.score > cur.peak.score) cur.peak = p;
    }
  }
  if (cur) runs.push(cur);
  return runs.sort((a, b) => b.peak.score - a.peak.score || a.start.localeCompare(b.start));
}

function renderPostureYearChartSvg(
  series: readonly AstrologyPosturePoint[],
  asOf: string,
  spxPoints: readonly AstrologySpxPoint[] = [],
  width = 760,
  height = 300,
): string {
  if (!series.length) {
    return `<p class="sfe-astro-empty">No posture history for this year.</p>`;
  }
  const year = Number(series[0]!.date.slice(0, 4));
  const padL = 44;
  const padR = spxPoints.length ? 48 : 16;
  const padT = 28;
  const padB = 48;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const maxScore = Math.max(6, ...series.map((p) => p.score));
  const n = series.length;
  const xAt = (i: number) => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (score: number) => padT + plotH - (score / maxScore) * plotH;
  const idxForDate = (ymd: string) => {
    const i = series.findIndex((p) => p.date === ymd);
    if (i >= 0) return i;
    let best = 0;
    for (let j = 0; j < series.length; j++) {
      if (series[j]!.date <= ymd) best = j;
      else break;
    }
    return best;
  };

  const todayIdx = asOf.startsWith(String(year)) ? idxForDate(asOf) : n - 1;
  const hasPrediction = todayIdx < n - 1;

  // S&P (SPY) as % from first close of the year — right axis.
  const spxInYear = spxPoints
    .filter((p) => p.date.startsWith(String(year)) && Number.isFinite(p.close) && p.close > 0)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));
  const spxBase = spxInYear[0]?.close ?? 0;
  const spxPct = spxBase > 0
    ? spxInYear.map((p) => ({
        date: p.date,
        pct: ((p.close / spxBase) - 1) * 100,
      }))
    : [];
  let spxMin = 0;
  let spxMax = 0;
  for (const p of spxPct) {
    spxMin = Math.min(spxMin, p.pct);
    spxMax = Math.max(spxMax, p.pct);
  }
  if (spxMin === spxMax) {
    spxMin -= 1;
    spxMax += 1;
  }
  // Pad a little so the line isn't glued to the edge.
  const spxPad = Math.max(1, (spxMax - spxMin) * 0.08);
  spxMin -= spxPad;
  spxMax += spxPad;
  const ySpx = (pct: number) => padT + plotH - ((pct - spxMin) / (spxMax - spxMin)) * plotH;

  const bands: Array<{ from: number; to: number; cls: string; label: string }> = [
    { from: 0, to: 1.5, cls: "calm", label: "Calm" },
    { from: 1.5, to: 3, cls: "watch", label: "Watch" },
    { from: 3, to: 5, cls: "caution", label: "Caution" },
    { from: 5, to: maxScore, cls: "alert", label: "Alert" },
  ];
  const bandRects = bands
    .map((b) => {
      const y1 = yAt(Math.min(b.to, maxScore));
      const y2 = yAt(b.from);
      const h = Math.max(0, y2 - y1);
      return `<rect class="sfe-astro-band sfe-astro-band-${b.cls}" x="${padL}" y="${y1.toFixed(1)}" width="${plotW}" height="${h.toFixed(1)}"></rect>
        <text class="sfe-astro-band-label" x="${(padL - 6).toFixed(1)}" y="${((y1 + y2) / 2 + 4).toFixed(1)}" text-anchor="end">${esc(b.label)}</text>`;
    })
    .join("");

  let predictShade = "";
  if (hasPrediction) {
    const x0 = xAt(todayIdx);
    const w = padL + plotW - x0;
    predictShade = `<rect class="sfe-astro-predict-shade" x="${x0.toFixed(1)}" y="${padT}" width="${Math.max(0, w).toFixed(1)}" height="${plotH}"></rect>
      <text class="sfe-astro-predict-caption" x="${(x0 + Math.max(0, w) / 2).toFixed(1)}" y="${(padT - 8).toFixed(1)}" text-anchor="middle">Predicted</text>`;
  }

  const observedPath = stepPathForRange(series, 0, todayIdx, xAt, yAt);
  // Connect prediction from today's point so the dashed segment starts cleanly.
  const predictPath = hasPrediction
    ? stepPathForRange(series, todayIdx, n - 1, xAt, yAt)
    : "";

  const ticks = monthTicks(year)
    .map((t) => {
      const i = idxForDate(t.date);
      const x = xAt(i);
      return `<line class="sfe-astro-grid" x1="${x.toFixed(1)}" y1="${padT}" x2="${x.toFixed(1)}" y2="${(padT + plotH).toFixed(1)}"></line>
        <text class="sfe-astro-tick" x="${x.toFixed(1)}" y="${(height - 28).toFixed(1)}" text-anchor="middle">${esc(t.label)}</text>`;
    })
    .join("");

  const peakMarks = predictedPeakRuns(series, asOf)
    .slice(0, 4)
    .map((run) => {
      const i = idxForDate(run.peak.date);
      const x = xAt(i);
      const y = yAt(run.peak.score);
      return `<circle class="sfe-astro-predict-peak sfe-astro-predict-peak-${esc(run.peak.posture)}" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.5"></circle>`;
    })
    .join("");

  let todayMark = "";
  if (asOf.startsWith(String(year))) {
    const x = xAt(todayIdx);
    const y = yAt(series[todayIdx]!.score);
    todayMark = `<line class="sfe-astro-today" x1="${x.toFixed(1)}" y1="${padT}" x2="${x.toFixed(1)}" y2="${(padT + plotH).toFixed(1)}"></line>
      <circle class="sfe-astro-today-dot" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5"></circle>
      <text class="sfe-astro-today-label" x="${x.toFixed(1)}" y="${(padT - 8).toFixed(1)}" text-anchor="${hasPrediction ? "end" : "middle"}">${hasPrediction ? "Today · " : ""}Today</text>`;
    if (hasPrediction) {
      // Avoid colliding with "Predicted" caption — put Today just left of the line.
      todayMark = `<line class="sfe-astro-today" x1="${x.toFixed(1)}" y1="${padT}" x2="${x.toFixed(1)}" y2="${(padT + plotH).toFixed(1)}"></line>
        <circle class="sfe-astro-today-dot" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5"></circle>
        <text class="sfe-astro-today-label" x="${(x - 6).toFixed(1)}" y="${(padT - 8).toFixed(1)}" text-anchor="end">Today</text>`;
    }
  }

  let spxPath = "";
  let spxAxis = "";
  if (spxPct.length >= 2) {
    spxPath = spxPct
      .map((p, i) => {
        const x = xAt(idxForDate(p.date));
        const y = ySpx(p.pct);
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
    const tickVals = [spxMin, 0, spxMax].filter((v, i, arr) => arr.indexOf(v) === i);
    // Prefer nice mid ticks when 0 is inside the range.
    if (spxMin < 0 && spxMax > 0 && !tickVals.includes(0)) tickVals.push(0);
    const nice = [...new Set(tickVals.map((v) => Math.round(v * 10) / 10))].sort((a, b) => a - b);
    spxAxis = nice
      .map((v) => {
        const y = ySpx(v);
        const label = `${v > 0 ? "+" : ""}${v.toFixed(0)}%`;
        return `<line class="sfe-astro-spx-grid" x1="${padL}" y1="${y.toFixed(1)}" x2="${(padL + plotW).toFixed(1)}" y2="${y.toFixed(1)}"></line>
          <text class="sfe-astro-spx-tick" x="${(padL + plotW + 6).toFixed(1)}" y="${(y + 3.5).toFixed(1)}">${esc(label)}</text>`;
      })
      .join("");
    spxAxis += `<text class="sfe-astro-spx-axis-label" x="${(padL + plotW + padR - 4).toFixed(1)}" y="${(padT - 10).toFixed(1)}" text-anchor="end">S&amp;P %</text>`;
  }

  const legend = `<g class="sfe-astro-legend" transform="translate(${padL}, ${height - 12})">
      <line class="sfe-astro-chart-line" x1="0" y1="-3" x2="18" y2="-3"></line>
      <text class="sfe-astro-legend-text" x="22" y="0">Posture</text>
      <line class="sfe-astro-chart-line sfe-astro-chart-line-predict" x1="78" y1="-3" x2="96" y2="-3"></line>
      <text class="sfe-astro-legend-text" x="100" y="0">Predicted</text>
      <line class="sfe-astro-spx-line" x1="168" y1="-3" x2="186" y2="-3"></line>
      <text class="sfe-astro-legend-text" x="190" y="0">S&amp;P 500</text>
      <circle class="sfe-astro-predict-peak sfe-astro-predict-peak-alert" cx="268" cy="-3" r="3"></circle>
      <text class="sfe-astro-legend-text" x="276" y="0">Peaks</text>
    </g>`;

  return `<svg class="sfe-astro-chart-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="Posture score, prediction, and S&P 500 for ${year}">
    ${bandRects}
    ${predictShade}
    ${spxAxis}
    ${ticks}
    ${observedPath ? `<path class="sfe-astro-chart-line" d="${observedPath}" fill="none"></path>` : ""}
    ${predictPath ? `<path class="sfe-astro-chart-line sfe-astro-chart-line-predict" d="${predictPath}" fill="none"></path>` : ""}
    ${spxPath ? `<path class="sfe-astro-spx-line" d="${spxPath}" fill="none"></path>` : ""}
    ${peakMarks}
    ${todayMark}
    ${legend}
  </svg>`;
}

function postureHistorySummary(series: readonly AstrologyPosturePoint[], asOf: string): string {
  const past = series.filter((p) => p.date <= asOf);
  const future = series.filter((p) => p.date > asOf);
  const counts: Record<AstrologyPosture, number> = { calm: 0, watch: 0, caution: 0, alert: 0 };
  let peakPast: AstrologyPosturePoint | null = null;
  for (const p of past) {
    counts[p.posture] += 1;
    if (!peakPast || p.score > peakPast.score) peakPast = p;
  }
  const total = past.length || 1;
  const pct = (n: number) => Math.round((n / total) * 100);
  const today = series.find((p) => p.date === asOf);
  const todayBit = today ? ` Today ${today.score.toFixed(1)} (${POSTURE_LABEL[today.posture]}).` : "";
  const pastPeakBit = peakPast
    ? ` YTD peak ${peakPast.score.toFixed(1)} on ${fmtDay(peakPast.date)}.`
    : "";

  const runs = predictedPeakRuns(series, asOf);
  let forecastBit = "";
  if (future.length && runs.length) {
    const top = runs[0]!;
    const range =
      top.start === top.end ? fmtDay(top.start) : `${fmtDay(top.start)} – ${fmtDay(top.end)}`;
    forecastBit = ` Forecast: next hot window ${range} (peak ${top.peak.score.toFixed(1)} ${POSTURE_LABEL[top.peak.posture]}).`;
  } else if (future.length) {
    forecastBit = " Forecast: no Caution/Alert clusters left this year.";
  }

  return `YTD mix: Calm ${pct(counts.calm)}% · Watch ${pct(counts.watch)}% · Caution ${pct(counts.caution)}% · Alert ${pct(counts.alert)}%.${todayBit}${pastPeakBit}${forecastBit}`;
}

function renderPredictedPeaksHtml(series: readonly AstrologyPosturePoint[], asOf: string): string {
  const runs = predictedPeakRuns(series, asOf).slice(0, 5);
  if (!runs.length) {
    return `<p class="sfe-astro-predict-empty">No Caution/Alert clusters predicted for the rest of this year.</p>`;
  }
  const rows = runs
    .map((run) => {
      const when =
        run.start === run.end ? fmtDay(run.start) : `${fmtDay(run.start)} – ${fmtDay(run.end)}`;
      const days = daysBetween(asOf, run.start);
      return `<li class="sfe-astro-predict-item sfe-astro-predict-item-${esc(run.peak.posture)}">
        <strong>${esc(POSTURE_LABEL[run.peak.posture])}</strong>
        <span class="sfe-astro-predict-when">${esc(when)}</span>
        <span class="sfe-astro-predict-meta">in ${esc(String(days))}d · peak ${esc(run.peak.score.toFixed(1))}</span>
      </li>`;
    })
    .join("");
  return `<div class="sfe-astro-predict-list">
    <h4>Predicted hot windows</h4>
    <ul>${rows}</ul>
  </div>`;
}

function ensurePostureChartModal(): HTMLElement {
  let modal = document.getElementById("sfe-astro-posture-modal");
  if (modal) return modal;
  modal = document.createElement("div");
  modal.id = "sfe-astro-posture-modal";
  modal.className = "sfe-astro-modal";
  modal.hidden = true;
  modal.innerHTML = `<div class="sfe-astro-modal-card" role="dialog" aria-modal="true" aria-labelledby="sfe-astro-posture-title">
    <div class="sfe-astro-modal-head">
      <h3 id="sfe-astro-posture-title">Posture history</h3>
      <button type="button" class="sfe-astro-modal-close" aria-label="Close">×</button>
    </div>
    <p class="sfe-astro-modal-lead"></p>
    <div class="sfe-astro-modal-chart"></div>
    <div class="sfe-astro-modal-predict"></div>
    <p class="sfe-astro-modal-foot">Gold = posture (left). Purple = S&amp;P 500 via SPY % from year open (right). Dashed blue = posture prediction from the calendar (not a price forecast).</p>
  </div>`;
  document.body.appendChild(modal);
  return modal;
}

let postureChartOpener: HTMLElement | null = null;
let postureChartBound = false;

function closePostureChart(): void {
  const modal = document.getElementById("sfe-astro-posture-modal");
  if (!modal || modal.hidden) return;
  modal.hidden = true;
  const opener = postureChartOpener;
  postureChartOpener = null;
  opener?.setAttribute("aria-expanded", "false");
  if (opener instanceof HTMLElement) opener.focus();
}

const spxYearCache = new Map<number, { at: number; points: AstrologySpxPoint[] }>();
const SPX_CACHE_MS = 30 * 60_000;

async function fetchSpxDailyForYear(year: number): Promise<AstrologySpxPoint[]> {
  const cached = spxYearCache.get(year);
  if (cached && Date.now() - cached.at < SPX_CACHE_MS) return cached.points;
  try {
    const { baseUrl } = getApiConfig();
    const base = baseUrl ? baseUrl.replace(/\/$/, "") : "";
    const res = await proxyFetch(`${base}/astrology/spx-daily?year=${encodeURIComponent(String(year))}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return cached?.points ?? [];
    const data = (await res.json()) as { ok?: boolean; points?: AstrologySpxPoint[] };
    const points = Array.isArray(data.points) ? data.points : [];
    spxYearCache.set(year, { at: Date.now(), points });
    return points;
  } catch {
    return cached?.points ?? [];
  }
}

function openPostureChart(button: HTMLElement): void {
  const year = Number(button.getAttribute("data-astro-year") || new Date().getUTCFullYear());
  const asOf = button.getAttribute("data-astro-asof") || ymdUTC(new Date());
  const series = buildPostureYearHistory(year);
  const modal = ensurePostureChartModal();
  const title = modal.querySelector("#sfe-astro-posture-title");
  const lead = modal.querySelector(".sfe-astro-modal-lead");
  const chart = modal.querySelector(".sfe-astro-modal-chart");
  const predictHost = modal.querySelector(".sfe-astro-modal-predict");
  if (title) title.textContent = `Posture + S&P 500 · ${year}`;
  if (lead) lead.textContent = postureHistorySummary(series, asOf);
  if (chart) {
    chart.innerHTML = `${renderPostureYearChartSvg(series, asOf)}<p class="sfe-astro-spx-loading">Loading S&amp;P 500…</p>`;
  }
  if (predictHost) predictHost.innerHTML = renderPredictedPeaksHtml(series, asOf);
  postureChartOpener?.setAttribute("aria-expanded", "false");
  postureChartOpener = button;
  button.setAttribute("aria-expanded", "true");
  modal.hidden = false;
  modal.querySelector<HTMLButtonElement>(".sfe-astro-modal-close")?.focus();

  void fetchSpxDailyForYear(year).then((points) => {
    if (postureChartOpener !== button || modal.hidden) return;
    if (!chart) return;
    const note = points.length
      ? ""
      : `<p class="sfe-astro-spx-loading">S&amp;P 500 series unavailable right now.</p>`;
    chart.innerHTML = `${renderPostureYearChartSvg(series, asOf, points)}${note}`;
    if (lead && points.length >= 2) {
      const first = points[0]!;
      const last = [...points].reverse().find((p) => p.date <= asOf) ?? points[points.length - 1]!;
      const pct = first.close > 0 ? ((last.close / first.close) - 1) * 100 : 0;
      const sign = pct >= 0 ? "+" : "";
      lead.textContent = `${postureHistorySummary(series, asOf)} S&P (SPY) YTD ${sign}${pct.toFixed(1)}% through ${fmtDay(last.date)}.`;
    }
  });
}

function bindPostureChart(): void {
  if (postureChartBound || typeof document === "undefined") return;
  postureChartBound = true;
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const openBtn = target.closest<HTMLElement>("[data-astro-posture-chart]");
    if (openBtn) {
      event.preventDefault();
      openPostureChart(openBtn);
      return;
    }
    const modal = document.getElementById("sfe-astro-posture-modal");
    if (!modal || modal.hidden) return;
    if (target.closest(".sfe-astro-modal-close") || target === modal) {
      event.preventDefault();
      closePostureChart();
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const modal = document.getElementById("sfe-astro-posture-modal");
    if (modal && !modal.hidden) {
      event.preventDefault();
      closePostureChart();
    }
  });
}

export function renderFinancialAstrologyHtml(snapshot: AstrologySnapshot): string {
  bindPostureChart();
  const activeHtml = snapshot.active.length
    ? snapshot.active.map((w) => renderWindowCard(w, "active")).join("")
    : `<p class="sfe-astro-empty">No major planetary windows active today — lunar rhythm still applies below.</p>`;
  const upcomingHtml = snapshot.upcoming.length
    ? snapshot.upcoming.map((w) => renderWindowCard(w, "upcoming")).join("")
    : `<p class="sfe-astro-empty">No curated windows in the next 90 days.</p>`;
  const playbookHtml = snapshot.playbook
    .map(
      (row) => `<div class="sfe-astro-play">
      <strong>${esc(row.title)}</strong>
      <p>${esc(row.body)}</p>
    </div>`,
    )
    .join("");
  const feet = snapshot.footnotes.map((f) => `<li>${esc(f)}</li>`).join("");

  return `<div class="sfe-astro">
    <header class="sfe-astro-head">
      <h2>Financial Astrology</h2>
      <p class="sfe-astro-asof">As of ${esc(fmtDay(snapshot.asOf))} (UTC) · timing calendar for wheel management</p>
    </header>

    <section class="sfe-astro-now" aria-label="Current posture">
      <button type="button" class="sfe-astro-score sfe-astro-score-btn sfe-astro-posture-${esc(snapshot.posture)}" data-astro-posture-chart data-astro-year="${esc(String(snapshot.year))}" data-astro-asof="${esc(snapshot.asOf)}" aria-expanded="false" aria-haspopup="dialog" title="Open ${esc(String(snapshot.year))} posture history">
        <p class="sfe-astro-kicker">Posture</p>
        <p class="sfe-astro-value">${esc(POSTURE_LABEL[snapshot.posture])}</p>
        <p class="sfe-astro-sub">Score ${esc(snapshot.score.toFixed(1))} · tap for ${esc(String(snapshot.year))} chart</p>
      </button>
      <div class="sfe-astro-score">
        <p class="sfe-astro-kicker">Moon</p>
        <p class="sfe-astro-value sfe-astro-value-sm">${esc(snapshot.lunar.phaseName)}</p>
        <p class="sfe-astro-sub">${esc(String(snapshot.lunar.illuminationPct))}% lit · new in ${esc(String(snapshot.lunar.daysToNew ?? "—"))}d · full in ${esc(String(snapshot.lunar.daysToFull ?? "—"))}d</p>
      </div>
    </section>

    <p class="sfe-astro-sentence">${esc(snapshot.sentence)}</p>

    <section class="sfe-astro-section">
      <h3>Active now</h3>
      <div class="sfe-astro-grid">${activeHtml}</div>
    </section>

    <section class="sfe-astro-section">
      <h3>Next 90 days</h3>
      <p class="sfe-astro-note-line">Eclipses and stacked retrogrades rank highest. Lunar turns are lighter bookmarks.</p>
      <div class="sfe-astro-grid">${upcomingHtml}</div>
    </section>

    <section class="sfe-astro-section">
      <h3>Playbook</h3>
      <div class="sfe-astro-playbook">${playbookHtml}</div>
    </section>

    <ul class="sfe-astro-foot">${feet}</ul>
  </div>`;
}

export function fetchFinancialAstrology(): Promise<AstrologySnapshot> {
  return Promise.resolve(buildFinancialAstrologySnapshot());
}

export class FinancialAstrologyController {
  private panel: HTMLElement | null = null;

  mount(panel: HTMLElement): void {
    this.panel = panel;
    panel.innerHTML = `<div class="sfe-astro"><p class="sfe-astro-status">Loading astrology…</p></div>`;
  }

  async refresh(): Promise<void> {
    if (!this.panel) return;
    try {
      const snapshot = await fetchFinancialAstrology();
      if (!this.panel) return;
      this.panel.innerHTML = renderFinancialAstrologyHtml(snapshot);
    } catch {
      if (!this.panel) return;
      this.panel.innerHTML = `<div class="sfe-astro"><p class="sfe-astro-status">Astrology calendar is unavailable.</p></div>`;
    }
  }
}
